import React from 'react';
import BellIcon from '../icons/BellIcon';

// 아이콘 타입 정의
type IconType = 'chat' | 'notification' | 'user';

// 아이콘 props 인터페이스
interface IconProps {
  type: IconType;           // 아이콘 종류
  onClick?: (event?: React.MouseEvent<HTMLElement>) => void;     // 클릭 시 실행할 함수
  className?: string;       // 추가 CSS 클래스
  light?: boolean;
}

// 개별 아이콘 컴포넌트
const Icon: React.FC<IconProps> = ({ type, onClick, className = '', light = false }) => {
  // Figma에서 가져온 아이콘 스타일
  const iconStyles = {
    width: 32,                              // Figma에서 가져온 아이콘 크기
    height: 32,                             // Figma에서 가져온 아이콘 크기
    position: 'relative' as const,          // 상대 위치
    justifyContent: 'space-between' as const, // 가로 분산 정렬
    alignItems: 'center' as const,          // 세로 중앙 정렬
    display: 'flex' as const,               // flexbox 레이아웃
    cursor: 'pointer',                      // 마우스 커서 변경
    transition: 'all 0.2s ease'             // 부드러운 전환 효과
  };

  // 아이콘 배경 스타일
  const backgroundStyles = {
    width: 32,                              // Figma에서 가져온 배경 크기
    height: 32,                             // Figma에서 가져온 배경 크기
    background: 'transparent',              // 배경 투명하게 설정
  };

  // 아이콘 내용 스타일
  const contentStyles = {
    width: 24,                              // Figma에서 가져온 내용 크기
    height: 24,                             // Figma에서 가져온 내용 크기
    left: 4,                                // Figma에서 가져온 위치
    top: 4,                                 // Figma에서 가져온 위치
    position: 'absolute' as const,          // 절대 위치
    justifyContent: 'flex-start' as const,  // 좌측 정렬
    alignItems: 'flex-start' as const,      // 상단 정렬
    display: 'flex' as const                // flexbox 레이아웃
  };

  // 아이콘별 내용 렌더링
  const renderIconContent = () => {
    switch (type) {
      case 'chat':
        return (
          <img 
            src="/images/icons/Property 1=Chat_Circle_Dots.svg" 
            alt="채팅" 
            style={{
              width: 24,
              height: 24,
              filter: light ? 'invert(1)' : undefined,
            }}
          />
        );
      
      case 'notification':
        return (
          <BellIcon width={24} height={24} fill={light ? '#F4F4F8' : '#0D0D0D'} />
        );
      
      case 'user':
        return (
          <img 
            src="/images/icons/name=user.svg" 
            alt="사용자" 
            style={{
              width: 24,
              height: 24,
              filter: light ? 'invert(1)' : undefined,
            }}
          />
        );
      
      default:
        return null;
    }
  };

  return (
    <div 
      style={iconStyles}
      onClick={onClick}
      className={className}
    >
      {/* 아이콘 내용만 표시 */}
      {renderIconContent()}
    </div>
  );
};

// 우측 아이콘들 props 인터페이스
interface RightIconsProps {
  onChatClick?: () => void;        // 채팅 아이콘 클릭 시 실행할 함수
  onNotificationClick?: () => void; // 알림 아이콘 클릭 시 실행할 함수
  onUserClick?: (event?: React.MouseEvent<HTMLElement>) => void;        // 사용자 아이콘 클릭 시 실행할 함수
  className?: string;               // 추가 CSS 클래스
  unreadNotificationCount?: number; // 읽지 않은 알림 개수
  light?: boolean;
}

// RightIcons 컴포넌트 정의
const RightIcons: React.FC<RightIconsProps> = ({ 
  onChatClick,           // 채팅 클릭 함수
  onNotificationClick,   // 알림 클릭 함수
  onUserClick,           // 사용자 클릭 함수
  className = '',
  unreadNotificationCount = 0,
  light = false,
}) => {
  return (
    <div 
      style={{
        // Figma에서 가져온 컨테이너 스타일
        justifyContent: 'flex-start' as const,
        alignItems: 'center' as const,
        gap: 12,                    // Figma에서 가져온 간격
        display: 'flex' as const
      }}
      className={className}
    >
      {/* 채팅 아이콘 */}
      <Icon 
        type="chat" 
        onClick={onChatClick}
        light={light}
      />
      
      {/* 알림 아이콘 */}
      <div style={{ position: 'relative' }}>
      <Icon 
        type="notification" 
        onClick={onNotificationClick}
        light={light}
        />
        {unreadNotificationCount > 0 && (
          <div
            style={{
              position: 'absolute',
              top: '0rem',
              right: '0.3rem',
              minWidth: '1.125rem',
              height: '1.125rem',
              padding: '0 0.25rem',
              borderRadius: '0.5625rem',
              backgroundColor: '#FF6F0F',
              color: '#FFFFFF',
              fontSize: '0.625rem',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
              zIndex: 2,
              boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.15)'
            }}
          >
            {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
          </div>
        )}
      </div>
      
      {/* 사용자 아이콘 — button으로 클릭 영역·포커스 확실히 */}
      <button
        type="button"
        aria-label="프로필 및 계정 메뉴 열기"
        onClick={(e) => onUserClick?.(e)}
        style={{
          padding: 0,
          margin: 0,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '8px'
        }}
      >
        <Icon type="user" light={light} />
      </button>
    </div>
  );
};

// RightIcons 컴포넌트를 다른 파일에서 사용할 수 있도록 내보내기
export default RightIcons;
